import { AuthTransaction } from '@mgmresorts/mgm-auth-js-sdk';
import { CircularProgress } from '@mgmresorts/mgm-ui';
import get from 'lodash.get';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { KnownError, UNKNOWN_ERROR_MESSAGE } from '../../api/errors';
import { updatePasswordRequest } from '../../api/fetch/update-password';
import { Confirmation } from '../../components/ui/Confirmation';
import { Container } from '../../components/ui/Container';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { captureEvent, captureException } from '../../monitoring/logrocket';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { AuthContext } from '../../utils/auth/AuthProvider';
import { redirectToSignIn } from '../../utils/redirect';

import { ResetPasswordForm } from './components/ResetPasswordForm';
import { Centered, StyledContent } from './styles';
import { useResetTokenVerification } from './useResetTokenVerification';

export type PolicyUserInfo = { firstName?: string; lastName?: string; login?: string };

export function ResetPassword(props: { token?: string }) {
  usePageLoadEvent('sign in', 'reset password');
  const trackEvent = useEvent();
  const { authClient } = useContext(AuthContext);
  const { verifyToken, isVerifyingToken } = useResetTokenVerification();
  const [transactionStep, setTransactionStep] = useState('init');
  const [authTransaction, setAuthTransaction] = useState<AuthTransaction | undefined>();
  const [error, setErrorState] = useState<null | string>(null);
  const { getContentByPath } = useContent();
  const [isLoading, setIsLoadingState] = useState(false);
  const [userInfo, setUserInfo] = useState<PolicyUserInfo | undefined>();

  useEffect(() => {
    if (userInfo) {
      trackEvent('identity – reset password - start', 'reset password process start', 'resetpstart');
    }
  }, [trackEvent, userInfo]);

  useEffect(() => {
    if (transactionStep === 'error') {
      trackEvent('identity – reset password - invalid token', 'invalid token error', 'resetptokenerror', {
        errorDisplayed: getContentByPath('resetPassword.errors.tokenValidationError'),
      });
    }
  }, [getContentByPath, trackEvent, transactionStep]);

  const resetPassword = useCallback(
    async (password: string) => {
      try {
        setIsLoadingState(true);
        if (!authTransaction || !authTransaction.stateToken) {
          throw Error('Invalid transaction');
        }

        captureEvent('Reset Password', {
          email: String(authTransaction?.user?.login),
        });

        const transaction = await updatePasswordRequest({
          newPassword: password,
          stateToken: authTransaction.stateToken,
        });

        if (transaction.status !== 'SUCCESS') {
          setErrorState(getContentByPath('resetPassword.errors.default'));
          throw Error('Failed to set a new password');
        }

        trackEvent(
          'identity – reset password - credentials submit success',
          'reset password submit success',
          'resetpsubmitsuccess',
          {
            resetPasswordState: 'success',
          },
        );

        setAuthTransaction(transaction);
        setTransactionStep('end');
      } catch (error) {
        let message;
        if (error instanceof KnownError) {
          message = error.message;
        } else {
          message = UNKNOWN_ERROR_MESSAGE;
        }

        trackEvent(
          'identity – reset password - credentials submit error',
          'reset password submit error',
          'resetpsubmitfail',
          {
            resetPasswordState: 'failed',
            errorDisplayed: message,
          },
        );

        setErrorState(message);
        captureException(error as Error, 'reset-password', {
          username: authTransaction?.user?.login,
          step: 'update-password',
        });
      } finally {
        setIsLoadingState(false);
      }
    },
    [authTransaction, getContentByPath, trackEvent],
  );

  const evaluateTokenFromUrl = useCallback(async () => {
    try {
      const tokenVerificationTransaction = await verifyToken(props.token);
      if (tokenVerificationTransaction.status === 'PASSWORD_RESET') {
        setTransactionStep('reset_password');
        const receivedUserInfo: { [key: string]: string } = get(
          tokenVerificationTransaction,
          '_embedded.user.profile',
          {},
        );
        setUserInfo(receivedUserInfo);
        setAuthTransaction(tokenVerificationTransaction);
        return;
      }
      setTransactionStep('error');
    } catch (err) {
      setErrorState(getContentByPath('resetPassword.errors.tokenValidationError'));
      setTransactionStep('error');
      captureException(err as Error, 'reset-password', {
        username: authTransaction?.user?.login,
        step: 'token-verification',
        token: String(props.token),
      });
    } finally {
      setIsLoadingState(false);
    }
  }, [authTransaction, getContentByPath, props.token, verifyToken]);

  useEffect(() => {
    const validDependencies = props.token && authClient && getContentByPath;
    if (validDependencies && transactionStep === 'init' && !isVerifyingToken) {
      evaluateTokenFromUrl();
    }
  }, [props.token, authClient, getContentByPath, evaluateTokenFromUrl, transactionStep, isVerifyingToken]);

  if (transactionStep === 'init') {
    return (
      <PageLayout
        testId='route-/reset-password/:token'
        title={getContentByPath('resetPassword.steps.reset.title')}
        description={getContentByPath('resetPassword.steps.reset.description')}
      >
        <Container>
          <Centered>
            <CircularProgress data-testid='reset-password-loading' size={48} indeterminate />
          </Centered>
        </Container>
      </PageLayout>
    );
  }

  if (transactionStep === 'end') {
    return (
      <Confirmation
        title={getContentByPath('resetPassword.steps.confirmation.title')}
        description={getContentByPath<string>('resetPassword.steps.confirmation.body')}
        redirectCTA={{
          label: getContentByPath('resetPassword.steps.confirmation.buttons.primaryAction.label'),
          action: async () => {
            await trackEvent(
              'identity – reset password - back to sign in',
              'back to sign in',
              'resetpbacktosignin',
              {},
              true,
            );
            redirectToSignIn();
          },
        }}
      />
    );
  }

  return (
    <StyledContent>
      <PageLayout
        testId='route-/reset-password/:token'
        title={getContentByPath('resetPassword.steps.reset.title')}
        description={getContentByPath('resetPassword.steps.reset.description')}
      >
        <ResetPasswordForm
          isLoading={isLoading || transactionStep === 'error'}
          error={error}
          userInfo={userInfo}
          resetPassword={resetPassword}
        />
      </PageLayout>
    </StyledContent>
  );
}

ResetPassword.displayName = 'ResetPassword';
